import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIngredientMatches } from '../../api/review';
import ProductFinder from './ProductFinder';
import { MatchRow } from './shared';

/** The match review for one line, opened from the shopping list.
 *
 * The review page is the right place to work through a queue, but the wrong
 * place to notice the problem: you find out the matcher picked pumpkin butter
 * standing in front of the butter. So the same controls open in place — the
 * alternates already found, and the finder for when none of them is it.
 *
 * Everything here is the review page's machinery unchanged, because the
 * verdicts mean the same thing wherever they are given: choosing an alternate
 * pins it for this shop, rejecting one rules that pairing out at every shop,
 * and both outrank the matcher on every future refresh. A correction made in
 * the aisle should not be a weaker correction.
 */
export default function ItemSwap({
  ingredientId,
  ingredientName,
  storeId,
  storeName,
  productName,
}: {
  ingredientId: number;
  ingredientName: string;
  /** The shop the list was built for; its shelf is the one being asked about. */
  storeId: number | null;
  storeName: string;
  /** What the list currently says to buy, if anything. */
  productName: string;
}) {
  const [open, setOpen] = useState(false);
  const {
    data: row,
    isLoading,
    isError,
    error,
  } = useIngredientMatches(ingredientId, storeId, open);

  /* A fragment rather than a wrapper: the toggle sits inline beside the
     line's other action, while the panel needs the full width underneath, and
     both are laid out by .item__actions. */
  return (
    <>
      <button
        type="button"
        className="link-button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? 'done swapping' : productName ? 'wrong product?' : 'find a product'}
      </button>

      {open && (
        <div className="swap__panel">
          {isLoading && <p className="muted small">Asking what else matched…</p>}

          {/* The list itself works offline, so this is the one part of the
              page that can fail in the aisle. It says so rather than sitting
              empty, which would read as "nothing else matched". */}
          {isError && (
            <p className="finder__error">
              Could not load the other matches — {(error as Error).message}
            </p>
          )}

          {row && row.matches.length > 0 && (
            <ul className="review-matches review-matches--compact">
              {row.matches.map((match) => (
                <MatchRow
                  key={match.id}
                  match={match}
                  ingredient={ingredientName}
                  /* One shop's shelf, named once at the top of the page. */
                  newStore={false}
                />
              ))}
            </ul>
          )}

          {row && row.matches.length === 0 && (
            <p className="muted small">
              Nothing matched {ingredientName} at {storeName} yet.
            </p>
          )}

          {/* The rest of what can be corrected about this ingredient — its
              aliases, what it is a kind of, a duplicate to fold in — is desk
              work, and lives where there is room for it. Offered only for an
              ingredient some recipe calls for, because that is all the queue
              lists: a substitute this kitchen keeps is on the list without
              being in any recipe, and the link would land on "nothing matches
              those filters". */}
          {!isLoading && !isError && (
            <>
              <ProductFinder
                ingredientId={ingredientId}
                ingredientName={ingredientName}
                storeId={storeId}
              />
              {row && row.uses > 0 && (
                <Link
                  className="muted small"
                  to={{
                    pathname: '/review/matches',
                    search: `?${new URLSearchParams({
                      search: ingredientName,
                      ...(storeId == null ? {} : { store: String(storeId) }),
                    })}`,
                  }}
                >
                  Open the full review for {ingredientName} →
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
